import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormID } from '@finxone-platform/form-action';
import {
  AlertDetails,
  GenerateOtpPayload,
  ImportUsers,
  ImportUsersPayload,
  KycCheckStatus,
  KycLevel,
  KycTokenResponse,
  NotificationSettings,
  NotificationSettingsPayload,
  OnboardUserResponse,
  OrganisationOnboardingRequest,
  OrganisationVerificationRequest,
  ResponseWithAlertCodesType,
  Roles,
  UserInfoType,
  UserPayload,
  UserProfileData,
  UsersList,
  VerificationFlag,
  VerificationOrganisationData,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import jwt_decode, { InvalidTokenError } from 'jwt-decode';
import { Observable, Subject, catchError, from, map, of, shareReplay, switchMap } from 'rxjs';
import { SetFormActionWithId } from '../../actions/form-submission.action';
import { RemoveProgressBarStack } from '../../actions/progress-bar.action';
import { FormActionState } from '../../state/form-submision.state';
import { AutoSuggestAddressResponseItems, FindAddressResponse } from '../../dtos/address.dto';
import { MotVerificationResponseItem } from '../../dtos/mot.dto';
import { ConfigService } from '../config-service/config-service.service';
import {
  BusinessOnboardingRequest,
  BusinessOnboardingResponse,
  CheckCompanyResponse,
  CheckPayeeVerificationRequest,
  CheckPayeeVerificationResponse,
  OnboardIndividualRequest,
  SecurePayloadDecodedResponse,
  VerifyOtpCommonResponse,
} from './metadata-service.type';
import { generateRequestHeaders } from './otp-utils';
import { CloudflareTurnstileService } from '../cloudflare-turnstile/cloudflare-turnstile.service';

@Injectable({
  providedIn: 'root',
})
export class MetadataService {
  private baseUrl = '';

  verificationFlag: Subject<VerificationFlag> = new Subject();
  // import user observable
  importselecteduser: Subject<ImportUsers> = new Subject();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private store: Store,
    private cloudflareTurnstileService: CloudflareTurnstileService,
  ) {
    this.configService.getApi('metadata_service').subscribe((api) => {
      this.baseUrl = api;
    });
  }

  verifyOtp(mobileNumber: string, verificationCode: string): Observable<boolean> {
    return this.http
      .post(
        this.baseUrl + '/users/verify-otp',
        {
          mobileNumber: mobileNumber,
          verificationCode: verificationCode,
        },
        { responseType: 'text' },
      )
      .pipe(
        map((v) => {
          return true;
        }),
        catchError<boolean, Observable<boolean>>((_err, _caught) => {
          console.error(`Error verifying otp: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  autosuggestAddress(address: string): Observable<AutoSuggestAddressResponseItems> {
    return this.http.get<AutoSuggestAddressResponseItems>(`${this.baseUrl}/address/autosuggest-address`, {
      params: { term: address },
    });
  }

  getAddressById(id: string): Observable<FindAddressResponse<[]>> {
    return this.http.get<FindAddressResponse<[]>>(`${this.baseUrl}/address/find-address`, {
      params: { id: id },
    });
  }

  verifyMot(registeredNumber: string): Observable<MotVerificationResponseItem> {
    return this.http.get<MotVerificationResponseItem>(this.baseUrl + '/mot/verification', {
      params: { 'registed-number': registeredNumber },
    });
  }

  onboardIndividual(
    form: OnboardIndividualRequest,
  ): Observable<ResponseWithAlertCodesType<OnboardUserResponse>> {
    return this.http
      .post<ResponseWithAlertCodesType<OnboardUserResponse>>(
        `${this.baseUrl}/users/onboarding-individual`,
        form,
      )
      .pipe(
        map((response: ResponseWithAlertCodesType<OnboardUserResponse>) => {
          return response;
        }),
        catchError((_err) => {
          console.error(`Error onboarding individual: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  checkPayeeVerification(request: CheckPayeeVerificationRequest): Observable<CheckPayeeVerificationResponse> {
    let optionalHeaders = {};
    if (this.cloudflareTurnstileService.getSiteKey()) {
      const token = this.cloudflareTurnstileService.getToken();
      if (token) {
        optionalHeaders = { 'X-Turnstile-Token': token };
      }
    }
    return this.http.get(this.baseUrl + '/users/user-info', { responseType: 'text' }).pipe(
      switchMap((secret) => {
        return from(generateRequestHeaders(secret)).pipe(
          switchMap((headers) => {
            return this.http.post<CheckPayeeVerificationResponse>(
              `${this.baseUrl}/verification/check-payee`,
              request,
              {
                headers: {
                  ...headers,
                  ...optionalHeaders,
                },
              },
            );
          }),
        );
      }),
    );
  }

  organisationOnboarding(form: OrganisationOnboardingRequest): Observable<string> {
    return this.http.post<{ id: string }>(`${this.baseUrl}/organisations`, form).pipe(
      map((response) => {
        return response.id;
      }),
      catchError<string, Observable<string>>((_err) => {
        console.error(`Error organisationOnboarding: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  /**
   * `POST /organisations/onboarding` — the directors/ownership business
   * onboarding endpoint, which creates the organisation, onboards the primary
   * member as its owner and stores the remaining members as pending invites.
   *
   * Since PR 6425 this is the *only* organisation onboarding endpoint —
   * `POST /organisations` (which `organisationOnboarding` above still targets)
   * was removed, its org-only behaviour absorbed here as an API-key-guarded
   * migration mode. Unprotected (no bearer needed), but self-service submits
   * must carry the single-use email/phone verification tokens minted by
   * `verify-otp-common` for the primary member, or the server answers 403
   * PROFILE_OTP_VERIFICATION_REQUIRED.
   */
  onboardBusiness(form: BusinessOnboardingRequest): Observable<BusinessOnboardingResponse> {
    return this.http.post<BusinessOnboardingResponse>(`${this.baseUrl}/organisations/onboarding`, form).pipe(
      catchError((_err) => {
        console.error(`Error onboarding business: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  associateUserOrganisation(orgId: string): Observable<boolean> {
    return this.http.put(`${this.baseUrl}/organisations/${orgId}/associate-user`, {}).pipe(
      map(() => {
        this.store.dispatch(
          new RemoveProgressBarStack({
            uniqueId: 'associateUserOrganisation',
          }),
        );
        return true;
      }),
      catchError<boolean, Observable<boolean>>((_err) => {
        this.store.dispatch(
          new RemoveProgressBarStack({
            uniqueId: 'associateUserOrganisation',
          }),
        );
        console.error(`Error associateUserOrganisation: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  checkEmail(email: string): Observable<boolean> {
    return this.http
      .post(
        this.baseUrl + '/users/check-email-exist',
        {
          email: email,
        },
        { responseType: 'text' },
      )
      .pipe(
        map((v) => {
          return true;
        }),
        catchError<boolean, Observable<boolean>>((_err, _caught) => {
          if (_err.status == 409 || _err.status == 400) {
            return of(false);
          } else {
            console.error(`Error while check email exist: ${JSON.stringify(_err)}`);
            throw _err;
          }
        }),
      );
  }

  checkCompanyNumber(company_number: string, company_name: string): Observable<CheckCompanyResponse> {
    return this.http
      .post<CheckCompanyResponse>(
        this.baseUrl + '/company/check-number',
        {
          company_number: company_number,
          company_name: company_name,
        },
        { responseType: 'json' },
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        catchError<any, Observable<boolean>>((_err, _caught) => {
          console.error(`Error fetching Check company number: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  updateProfile(form: UserProfileData): Observable<boolean> {
    return this.http
      .post(`${this.baseUrl}/users/update-user-profile`, form, {
        responseType: 'text',
      })
      .pipe(
        map(() => {
          return true;
        }),
        catchError<boolean, Observable<boolean>>((_err, _caught) => {
          console.error(`Error onboarding individual: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  updateAttributes(attributes: object): Observable<boolean> {
    return this.http.patch(`${this.baseUrl}/users/attributes`, attributes).pipe(
      map(() => {
        return true;
      }),
      catchError<boolean, Observable<boolean>>((_err, _caught) => {
        console.error(`Error updating attributes: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  // http://localhost:8002/user?page=1&size=10&sort=DESC&projectId=5471e302-216e-4ba9-b554-7dd3ee895516&sortKey=lastName
  getUsers(
    page: number,
    size: number,
    sortKey: string,
    sortOrder: string,
    search: string,
  ): Observable<UsersList> {
    return this.http.get<UsersList>(
      `${this.baseUrl}/users?page=${page}&size=${size}&sortKey=${sortKey}&sortOrder=${sortOrder}&query=${search}`,
    );
  }

  addUser(form: UserPayload): Observable<boolean> {
    return this.http.post(`${this.baseUrl}/users`, form).pipe(
      map(() => {
        return true;
      }),
      catchError<boolean, Observable<boolean>>((_err, _caught) => {
        console.error(`Error onboarding individual: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  public setActiveThemeForUser(form: { themeName: string }): Observable<boolean> {
    return this.http.post(`${this.baseUrl}/users/update-theme`, form).pipe(
      map(() => {
        return true;
      }),
      catchError<boolean, Observable<boolean>>((_err) => {
        console.error(`Error setting active theme for the user: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  getRoles(): Observable<Roles[]> {
    return this.http.get<Roles[]>(`${this.baseUrl}/roles`);
  }

  getImportUsers(form: ImportUsersPayload): Observable<ImportUsers[]> {
    return this.http.post<ImportUsers[]>(`${this.baseUrl}/importusers/autosuggest`, form);
  }
  getUser(id?: string): Observable<UserInfoType> {
    const token = sessionStorage.getItem('token') as string;
    if (!token) {
      return of();
    }

    try {
      // Get last selected org from localStorage
      const lastSelectedOrg = localStorage.getItem(`lastSelectedOrg`) ?? '';
      let options = {};

      const organisationsAndUserIdInToken = this.extractOrgIdsAndUserId(token);
      const userId = id || organisationsAndUserIdInToken.userId;
      const organisationInToken = organisationsAndUserIdInToken.organisations;
      const savedLastSelectedOrg = lastSelectedOrg.split(':');

      // Set X-Finxone-Org-Id header to restore user's last selected organization on login.
      // This header overrides the default behavior (which would select the first org) and
      // ensures the backend returns user details with the correct organization selected.
      // The interceptor preserves this header, allowing it to reach the backend before
      // profile state is initialized. Validation ensures the org belongs to the current user.
      if (organisationInToken.includes(savedLastSelectedOrg[1]) && savedLastSelectedOrg[0] === userId) {
        options = {
          headers: {
            'X-Finxone-Org-Id': savedLastSelectedOrg[1],
          },
        };
      }

      return this.http.get<UserInfoType>(`${this.baseUrl}/users/${userId}`, options);
    } catch (error) {
      if (error instanceof InvalidTokenError) {
        console.error('Token decode issue when fetching user data');
      } else {
        console.error('getUser', error);
      }
      return of();
    }
  }

  updateUser(form: UserPayload, addProfileStatus = false): Observable<boolean> {
    form.profileStatus = addProfileStatus;
    return this.http
      .post(`${this.baseUrl}/users`, form, {
        responseType: 'text',
      })
      .pipe(
        map(() => {
          return true;
        }),
        catchError<boolean, Observable<boolean>>((_err, _caught) => {
          console.error(`Error onboarding individual: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public updateUserProfileData(payload: Partial<UserProfileData>): Observable<UserInfoType> {
    return this.http.patch<UserInfoType>(`${this.baseUrl}/users`, payload);
  }

  getKycAccessToken(type: KycLevel): Observable<KycTokenResponse> {
    return this.http.post<KycTokenResponse>(`${this.baseUrl}/verification-provider/get-token`, {
      type: type,
    });
  }

  setCompanyInfo(id: string): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}/verification-provider/update-applicant-company?id=${id}`,
      {},
    );
  }

  checkApplicantStatus(type: KycLevel): Observable<KycCheckStatus> {
    return this.http.get<KycCheckStatus>(`${this.baseUrl}/verification-provider/check-status?level=${type}`);
  }

  generateOtpPhoneEmail(payload: GenerateOtpPayload): Observable<boolean> {
    // Add optional turnstile header when siteKey exists and a valid token is available
    let optionalHeaders = {};
    if (this.cloudflareTurnstileService.getSiteKey()) {
      const token = this.cloudflareTurnstileService.getToken();
      if (token) {
        optionalHeaders = { 'X-Turnstile-Token': token };
      }
    }
    return this.http.get(this.baseUrl + '/users/user-info', { responseType: 'text' }).pipe(
      // First switchMap to flatten the outer Observable
      switchMap((secret) => {
        return from(generateRequestHeaders(secret)).pipe(
          // Second switchMap to flatten the inner Observable
          switchMap((headers) => {
            return this.http
              .post(this.baseUrl + '/users/generate-otp-common', payload, {
                headers: {
                  ...headers,
                  ...optionalHeaders,
                },
                responseType: 'text',
              })
              .pipe(
                map(() => true),
                catchError((error) => {
                  console.error(`Error generating otp:`, error);
                  throw error;
                }),
              );
          }),
        );
      }),
    );
  }

  verifyOtpPhoneEmail(payload: GenerateOtpPayload): Observable<boolean> {
    return this.http
      .post<VerifyOtpCommonResponse>(this.baseUrl + '/users/verify-otp-common', payload, {})
      .pipe(
        map((response) => {
          this.storeVerificationToken(payload, response);
          return true;
        }),
        catchError<boolean, Observable<boolean>>((_err, _caught) => {
          console.error(`Error verifying otp: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  /**
   * Keeps the single-use verification token the server minted so the onboarding submit can present
   * it again. Onboarding has no authenticated user, so the server's `verified_contact` cache entry
   * has nothing but this token binding it to the client that actually passed the OTP.
   *
   * A `userId` on the payload means the caller is signed in (profile update, payment SCA); those
   * flows are already bound by userId server-side and need no token.
   *
   * The token is written into the same store slice and with the same lifetime as the existing
   * `emailVerifiedAt` / `phoneVerifiedAt` markers, so it survives exactly the reloads and resumes
   * they survive — anything that loses them already forces re-verification.
   */
  private storeVerificationToken(payload: GenerateOtpPayload, response: VerifyOtpCommonResponse): void {
    if (payload.userId || !response?.verificationToken) {
      return;
    }
    const tokenKey = payload.fieldType === 'email' ? 'emailVerificationToken' : 'phoneVerificationToken';
    const formId = payload.formId ?? FormID.ONBOARDING_FLOW;
    this.store.dispatch(
      new SetFormActionWithId({ type: '', formData: { [tokenKey]: response.verificationToken } }, formId),
    );
  }

  /**
   * The tokens stashed by the most recent onboarding OTP verifications, for callers that build the
   * `onboarding-individual` payload themselves rather than going through
   * `transformUserFormDataIntoRequestPayload`. Both are required by the server when there is no
   * authenticated user; a missing one produces a 403 `PROFILE_OTP_VERIFICATION_REQUIRED`.
   */
  getOnboardingVerificationTokens(): {
    emailVerificationToken?: string;
    phoneVerificationToken?: string;
  } {
    const formData = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(FormID.ONBOARDING_FLOW),
    )?.formData;
    return {
      emailVerificationToken: formData?.['emailVerificationToken'] ?? undefined,
      phoneVerificationToken: formData?.['phoneVerificationToken'] ?? undefined,
    };
  }

  verifyEmailToken(token: string): Observable<boolean> {
    return this.http.get(this.baseUrl + `/users/verify-email-token/${token}`).pipe(
      map((v) => {
        return true;
      }),
      catchError<boolean, Observable<boolean>>((_err, _caught) => {
        console.error(`Error verifying token: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  // shared across the many notification-widget instances on a settings page so
  // they all ride one HTTP request instead of one each
  private notificationSettings$?: Observable<NotificationSettings>;

  getNotificationSettings(): Observable<NotificationSettings> {
    if (!this.notificationSettings$) {
      this.notificationSettings$ = this.http
        .get<NotificationSettings>(this.baseUrl + '/users/settings')
        .pipe(shareReplay(1));
    }
    return this.notificationSettings$;
  }

  updateNotificationSettings(
    payload: Partial<NotificationSettingsPayload>,
  ): Observable<NotificationSettings> {
    return this.http.post<NotificationSettings>(this.baseUrl + '/users/settings', payload).pipe(
      map((response) => {
        this.notificationSettings$ = of(response);
        return response;
      }),
      catchError((_err) => {
        console.error(`Error updating notification settings: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  getOrganisation(orgId: string): Observable<VerificationOrganisationData> {
    return this.http.get<VerificationOrganisationData>(`${this.baseUrl}/organisations/${orgId}`);
  }

  updateOrganisation(orgId: string, data: any): Observable<OrganisationVerificationRequest> {
    const payload = {
      name: data?.organisation_companyName,
      country: data?.addressCountry,
      address1: data?.address1,
      address2: data?.address2,
      address3: data?.address3,
      address4: data?.address4,
      city: data?.addressCity,
      state: data?.addressState,
      postalCode: data?.addressPostCode,
      extraAttributes: {},
      identificationNumber: data?.identificationNumber,
      incorporationDate: data?.incorporationDate,
    };
    return this.http.patch<OrganisationVerificationRequest>(
      `${this.baseUrl}/organisations/${orgId}`,
      payload,
    );
  }

  public getSystemAlerts(): Observable<{
    [errorCode: string]: AlertDetails;
  }> {
    return this.configService.getSystemAlerts();
  }

  public extractOrgIdsAndUserId(token: string): { organisations: string[]; userId: string } {
    if (token === '') {
      return { organisations: [], userId: '' };
    }

    try {
      const decodedToken: {
        organisation: { [orgId: string]: { roles: string[]; name: string } };
        sub: string;
      } = jwt_decode(token);
      return { organisations: Object.keys(decodedToken.organisation), userId: decodedToken.sub };
    } catch (error) {
      console.error('Failed to decode token:', error);
      return { organisations: [], userId: '' };
    }
  }

  public resolveSecurePayload(token: string): Observable<SecurePayloadDecodedResponse> {
    return this.http
      .post<{
        success: boolean;
        data: SecurePayloadDecodedResponse;
      }>(`${this.baseUrl}/resolve-secure-payload`, {
        token,
      })
      .pipe(map((response) => response.data));
  }
}
